import { useEffect, useRef, useState } from "react";
import { initializeMap, changeMapStyle } from "../../utils/MapboxUtils";

export const DEFAULT_STYLE = "mapbox://styles/mapbox/streets-v12";

// Creates the Mapbox map, keeps its canvas sized, and handles basemap changes.
const useMapbox = (mapRef, mapContainerRef) => {
  const [mapReady, setMapReady] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(DEFAULT_STYLE);

  /* ---- basemap change: a new style wipes every custom source/layer ---- */
  const styleLoading = useRef(false); // true between setStyle() and style.load
  const [styleVersion, setStyleVersion] = useState(0); // bumps on each style.load

  /* ------------------------------ map init ------------------------------ */
  useEffect(() => {
    initializeMap(mapRef, mapContainerRef, DEFAULT_STYLE, () =>
      setMapReady(true),
    );
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // The map container is sized by CSS (flex), so we only tell Mapbox to resize.
  useEffect(() => {
    const handleResize = () => mapRef.current?.resize();
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // keep the canvas in sync while the panel slides open/closed
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => mapRef.current?.resize());
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* one persistent listener: each time a style finishes loading,
     bump styleVersion so every sync effect re-adds its layers */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const onStyleLoad = () => {
      styleLoading.current = false;
      setStyleVersion((v) => v + 1);
    };
    map.on("style.load", onStyleLoad);
    return () => map.off("style.load", onStyleLoad);
  }, [mapReady]);

  const handleStyleChange = (url) => {
    if (url === selectedStyle) return;
    styleLoading.current = true;
    changeMapStyle(mapRef.current, url); // diff:false -> style.load always fires
    setSelectedStyle(url);
  };

  return { mapReady, styleVersion, styleLoading, selectedStyle, handleStyleChange };
};

export default useMapbox;
